import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { CustomerService } from '../../core/generated/model/customerService';
import { CustomerServiceControllerService, UserRef } from '../../core/generated';
import { ContainerComponent } from '../../shared/container/container.component';
import { MaterialModule } from '../../shared/material/material.module';
import { DialogChatComponent } from '../../shared/dialogs/dialog-chat/dialog-chat.component';
import { MatDialog } from '@angular/material/dialog';
import { DialogRegisterCustomerServiceComponent } from '../../shared/dialogs/dialog-register-customer-service/dialog-register-customer-service.component';
import { MatTable } from '@angular/material/table';
import { DeviceDetectorService } from 'ngx-device-detector';
import { register, SwiperContainer } from 'swiper/element';
import { FormControl, FormsModule } from '@angular/forms';
import { UserPreferencesCacheService } from '../../shared/services/user-preferences-cache.service';
import { AuthCustomService } from '../../security/auth-custom.service';
import { firstValueFrom } from 'rxjs';
import { EnumDescriptionPipe } from '../../shared/pipes/enum-description.pipe';
register();

export interface DialogData {
  newCustomerService: CustomerService;
}

@Component({
    selector: 'app-customer-service',
    standalone: true,
    imports: [MaterialModule, FormsModule, EnumDescriptionPipe, ContainerComponent],
    templateUrl: './customer-service.component.html',
    styleUrl: './customer-service.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CustomerServiceComponent implements OnInit {
  customerServiceService = inject(CustomerServiceControllerService);
  deviceService = inject(DeviceDetectorService);
  dialog = inject(MatDialog);
  UserPreferencesCacheService = inject(UserPreferencesCacheService);
  cd = inject(ChangeDetectorRef);
  authCustomService = inject(AuthCustomService);

  selectedTab = new FormControl('');
  @ViewChild('swiper') swiperRef!: ElementRef<SwiperContainer>;
  newCustomerService!: CustomerService;
  @ViewChild(MatTable) table!: MatTable<CustomerService>;
  isMobile!: boolean;
  displayedColumnsOpen: string[] = [
    'id',
    'subject',
    'registerDateOpen',
    'status',
    'buttons',
  ];
  displayedColumnsClose: string[] = [
    'id',
    'subject',
    'registerDateOpen',
    'registerDateClose',
    'status',
    'buttons',
  ];
  user!: UserRef;
  customerServices: CustomerService[] = [];
  isLoading = true;

  async ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    if (this.isMobile) {
      this.displayedColumnsOpen = ['mobile', 'buttons'];
      this.displayedColumnsClose = ['mobile', 'buttons'];
    }

    try {
      // await the first emission from the user observable
      this.user = await firstValueFrom(this.UserPreferencesCacheService.getUser());
    } catch (err) {
      console.error('Failed to get user from UserPreferencesCacheService', err);
      this.user = undefined!;
    }

    if (this.user && this.user.cpf) {
      this.customerServiceService.findByCpf1(this.user.cpf!).subscribe({
        next: (cs: any) => {
          this.customerServices = Array.isArray(cs) ? cs : (cs?.content ?? cs?.data ?? []);
          this.isLoading = false;
          this.cd.detectChanges();
          this.table?.renderRows();
        },
        error: (err) => {
          console.error('Error loading customer services', err);
          this.customerServices = [];
          this.isLoading = false;
          this.cd.detectChanges();
        },
      });
    } else {
      // no user or no cpf
      this.customerServices = [];
      this.isLoading = false;
      this.cd.detectChanges();
    }

    this.selectedTab.setValue('tab1');
  }

  ngAfterViewInit() {
    this.initializeSwiperWithRetry();
  }

  private initializeSwiperWithRetry(attempts = 0, maxAttempts = 10) {
    setTimeout(() => {
      const swiperInstance = this.swiperRef?.nativeElement?.swiper;
      
      if (swiperInstance) {
        this.setupSwiperListeners(swiperInstance);
      } else {
        if (attempts < maxAttempts) {
          this.initializeSwiperWithRetry(attempts + 1, maxAttempts);
        }
      }
    }, 200 * (attempts + 1));
  }

  private setupSwiperListeners(swiperInstance: any) {
    this.selectedTab.valueChanges.subscribe((value) => {
      const index = ({
        tab1: 0,
        tab2: 1,
      } as Record<string, number>)[value ?? 'tab1'];

      if (typeof index === 'number' && swiperInstance.activeIndex !== index) {
        swiperInstance.slideTo(index);
      }
    });

    swiperInstance.on('slideChange', () => {
      const idx = swiperInstance.activeIndex;
      const tabValue = ['tab1', 'tab2'][idx] || 'tab1';

      if (this.selectedTab.value !== tabValue) {
        this.selectedTab.setValue(tabValue, { emitEvent: false });
        this.cd.detectChanges();
      }
    });
  }

  createRegisterCustomerService() {
    const dialogRef = this.dialog.open(DialogRegisterCustomerServiceComponent, {
      // para expandir o diálogo na largura da tela

      data: { customerService: this.newCustomerService },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.customerServices.push(result);
        this.cd.detectChanges();
        this.table.renderRows();
      }
    });
  }

  countNotRead(id: number | undefined) {
    // Funcionalidade de contagem de mensagens não lidas foi removida
    // pois chats não fazem mais parte do modelo CustomerService
    return undefined;
  }

  openDialogChat(custumerServiceId: number | undefined) {
    if (!custumerServiceId) {
      return;
    }
    
    let custumerService = this.customerServices.find(
      (cs) => cs.id == custumerServiceId
    );

    const dialogConfig: any = {
      data: {
        customerService: custumerService,
      },
    };

    if (this.isMobile) {
      dialogConfig.position = { bottom: '0px' };
    }

    const dialogRef = this.dialog.open(DialogChatComponent, dialogConfig);

    dialogRef.afterClosed().subscribe();
    this.cd.detectChanges();
  }

  setWasRead(CSId: number) {
    // Funcionalidade removida - chats não fazem mais parte do modelo CustomerService
  }

  getCustomerServiceOpen() {
    return Array.isArray(this.customerServices)
      ? this.customerServices.filter((cs) => !cs.registerDateClose)
      : [];
  }

  getCustomerServiceClose() {
    return Array.isArray(this.customerServices)
      ? this.customerServices.filter((cs) => !!cs.registerDateClose)
      : [];
  }
}
